import React from 'react'
import { Icon } from 'semantic-ui-react'
import { Card, Divider, Stack, Typography } from '@prometheus/components'
import { truncateCash } from '@prometheus/utils'
import { useSelector } from '../../../../hooks/useSelector'
import { useGameBoardActions } from '../../hooks/useGameBoardActions'
import { useBossPanelHidden } from '../../hooks/useBossPanelHidden'
import { BossAttackForm } from './BossAttackForm'
import { BossSprite } from './BossSprites'
import './BossPanel.scss'

/**
 * The war room's content, shared by the middle-column panel, the header
 * chip's popup (`compact`) and nothing else — one body, so the two surfaces
 * can never disagree. All colors run through `--boss-ink` (see the scss):
 * race colors are picked for the black board, so the light theme derives a
 * darker ink instead of printing pale-on-pale.
 */
export const BossPanelBody = ({ compact }: { compact?: boolean }) => {
  const boss = useSelector(s => s.game.metadata?.boss)
  const players = useSelector(s => s.game.metadata?.players)
  const { focusPlanet } = useGameBoardActions()

  if (!boss?.alive) {
    return null
  }

  const healthPercent = boss.max_health > 0 ? Math.max(0, Math.min(100, (boss.health / boss.max_health) * 100)) : 0
  const damageDealt = Object.entries(boss.damage_by_player)
    .filter(([, damage]) => damage > 0)
    .sort(([, a], [, b]) => b - a)

  return (
    <Stack space="sm">
      <div className="pg-boss-panel__head">
        <BossSprite color={boss.color} width={compact ? 36 : 45} className="pg-boss-panel__sprite" />
        <Typography variant="h4" retro className="pg-boss-panel__race">{boss.race}</Typography>
        <span className="pg-boss-panel__level">LVL {boss.level}</span>
      </div>
      <div
        className="pg-boss-panel__health"
        role="progressbar"
        aria-label={`${boss.race} health`}
        aria-valuemin={0}
        aria-valuemax={boss.max_health}
        aria-valuenow={boss.health}
      >
        <div className="pg-boss-panel__health-fill" style={{ width: `${healthPercent}%` }} />
      </div>
      <Typography variant="caption">
        {truncateCash(boss.health, { raw: true })} / {truncateCash(boss.max_health, { raw: true })} hull
      </Typography>
      {!compact && (
        <Typography variant="body2">
          Mothership over{' '}
          <button type="button" className="pg-boss-panel__anchor" onClick={() => focusPlanet(boss.anchor_planet as string)}>
            {boss.anchor_planet}
          </button>{' '}
          in the {boss.quadrant} quadrant. It drains cash from every player each year and grows
          stronger the longer it stays — it has taken {truncateCash(boss.total_levied)} so far
          {boss.planets_destroyed > 0 ? ` and destroyed ${boss.planets_destroyed} ${boss.planets_destroyed > 1 ? 'colonies' : 'colony'}` : ''}.
          Destroy it to end the levy; the bounty is split by damage dealt.
        </Typography>
      )}
      <Divider />
      <BossAttackForm />
      {damageDealt.length > 0 && (
        <div className="pg-boss-panel__scores">
          {damageDealt.map(([uid, damage]) => (
            <div className="pg-boss-panel__score" key={uid}>
              <span>{players?.[uid]?.name ?? uid}</span>
              <span>{truncateCash(damage, { raw: true })} dmg</span>
            </div>
          ))}
        </div>
      )}
    </Stack>
  )
}

/**
 * The middle-column mount (desktop middle column / phone Turn tab, only while
 * `boss.alive`). Hiding it collapses the invasion into the header chip
 * (`BossHeaderChip`), where hover (desktop) or tap (phone) reopens this same
 * body as a popup.
 */
export const BossPanel = () => {
  const boss = useSelector(s => s.game.metadata?.boss)
  const [, setHidden] = useBossPanelHidden()

  if (!boss?.alive) {
    return null
  }

  return (
    <Card className="pg-boss-panel" style={{ '--boss-color': boss.color } as React.CSSProperties}>
      <button
        type="button"
        className="pg-boss-panel__hide"
        title="Hide — the invasion moves to the header"
        aria-label="Hide the invasion panel (it moves to the header)"
        onClick={() => setHidden(true)}
      >
        <Icon name="window minimize outline" />
      </button>
      <BossPanelBody />
    </Card>
  )
}
